import * as React from 'react';
import { sourceSystemApi } from '../services/sourceSystemApi';
import type {
  RepoBrowserFileDTO,
  RepoBrowserTreeDTO,
  RepoBrowserTreeNode,
} from '../types/source-system';

function findNodeById(nodes: RepoBrowserTreeNode[], nodeId: string): RepoBrowserTreeNode | null {
  for (const node of nodes) {
    if (node.id === nodeId) return node;
    const childMatch = findNodeById(node.children, nodeId);
    if (childMatch) return childMatch;
  }
  return null;
}

function findFirstFileNode(nodes: RepoBrowserTreeNode[]): RepoBrowserTreeNode | null {
  for (const node of nodes) {
    if (node.kind === 'file') return node;
    const nested = findFirstFileNode(node.children);
    if (nested) return nested;
  }
  return null;
}

function createInitialExpandedState(tree: RepoBrowserTreeDTO | null): Set<string> {
  if (!tree) return new Set<string>();
  return new Set(tree.roots.map((root) => root.id));
}

export interface RepoBrowserEditorSelection {
  startLineNumber: number;
  startColumn: number;
  endLineNumber: number;
  endColumn: number;
}

export function useRepoBrowser(projectUuid: string, refreshKey: string | null) {
  const [tree, setTree] = React.useState<RepoBrowserTreeDTO | null>(null);
  const [treeLoading, setTreeLoading] = React.useState(true);
  const [treeError, setTreeError] = React.useState<string | null>(null);
  const [selectedNodeId, setSelectedNodeId] = React.useState<string | null>(null);
  const [selectedFile, setSelectedFile] = React.useState<RepoBrowserFileDTO | null>(null);
  const [fileLoading, setFileLoading] = React.useState(false);
  const [fileError, setFileError] = React.useState<string | null>(null);
  const [expandedNodeIds, setExpandedNodeIds] = React.useState<Set<string>>(new Set());
  const [selectedFileIds, setSelectedFileIds] = React.useState<string[]>([]);
  const [selectedDirectoryIds, setSelectedDirectoryIds] = React.useState<string[]>([]);
  const [editorSelection, setEditorSelection] = React.useState<RepoBrowserEditorSelection | null>(
    null
  );
  const [editorSelectedText, setEditorSelectedText] = React.useState('');

  const loadTree = React.useCallback(
    async (quiet = false) => {
      if (!quiet) setTreeLoading(true);
      setTreeError(null);
      try {
        const data = await sourceSystemApi.getRepoBrowserTree(projectUuid);
        setTree(data);
        setExpandedNodeIds((current) => {
          if (current.size > 0) return current;
          return createInitialExpandedState(data);
        });
      } catch (err) {
        setTreeError((err as Error).message);
      } finally {
        if (!quiet) setTreeLoading(false);
      }
    },
    [projectUuid]
  );

  const loadFile = React.useCallback(
    async (nodeId: string) => {
      setFileLoading(true);
      setFileError(null);
      try {
        const data = await sourceSystemApi.getRepoBrowserFile(projectUuid, nodeId);
        setSelectedFile(data);
      } catch (err) {
        setFileError((err as Error).message);
        setSelectedFile(null);
      } finally {
        setFileLoading(false);
      }
    },
    [projectUuid]
  );

  const selectNode = React.useCallback(
    async (node: RepoBrowserTreeNode) => {
      setSelectedNodeId(node.id);

      if (node.kind === 'file') {
        setSelectedFileIds([node.id]);
        setSelectedDirectoryIds([]);
        setEditorSelection(null);
        setEditorSelectedText('');
        await loadFile(node.id);
        return;
      }

      setSelectedFile(null);
      setFileError(null);
      setSelectedFileIds([]);
      setSelectedDirectoryIds(node.kind === 'directory' ? [node.id] : []);
      setEditorSelection(null);
      setEditorSelectedText('');
    },
    [loadFile]
  );

  const toggleNode = React.useCallback((nodeId: string) => {
    setExpandedNodeIds((current) => {
      const next = new Set(current);
      if (next.has(nodeId)) next.delete(nodeId);
      else next.add(nodeId);
      return next;
    });
  }, []);

  const updateEditorSelection = React.useCallback(
    (selection: RepoBrowserEditorSelection | null, selectedText: string) => {
      setEditorSelection(selection);
      setEditorSelectedText(selectedText);
    },
    []
  );

  const clearSelection = React.useCallback(() => {
    setSelectedNodeId(null);
    setSelectedFile(null);
    setSelectedFileIds([]);
    setSelectedDirectoryIds([]);
    setEditorSelection(null);
    setEditorSelectedText('');
  }, []);

  React.useEffect(() => {
    void loadTree();
  }, [loadTree]);

  React.useEffect(() => {
    if (refreshKey === null) return;
    void loadTree(true);
  }, [loadTree, refreshKey]);

  React.useEffect(() => {
    if (!refreshKey || !selectedNodeId?.startsWith('file:')) return;
    void loadFile(selectedNodeId);
  }, [loadFile, refreshKey, selectedNodeId]);

  React.useEffect(() => {
    if (!tree) return;

    if (selectedNodeId) {
      const match = findNodeById(tree.roots, selectedNodeId);
      if (match) return;
    }

    const firstFile = findFirstFileNode(tree.roots);
    if (firstFile) {
      void selectNode(firstFile);
      return;
    }

    setSelectedNodeId(null);
    setSelectedFile(null);
    setSelectedFileIds([]);
    setSelectedDirectoryIds([]);
  }, [selectNode, selectedNodeId, tree]);

  const selectedNode = React.useMemo(() => {
    if (!tree || !selectedNodeId) return null;
    return findNodeById(tree.roots, selectedNodeId);
  }, [selectedNodeId, tree]);

  return {
    tree,
    treeLoading,
    treeError,
    selectedNode,
    selectedNodeId,
    selectedFile,
    fileLoading,
    fileError,
    expandedNodeIds,
    selectedFileIds,
    selectedDirectoryIds,
    editorSelection,
    editorSelectedText,
    refresh: loadTree,
    refetchFile: loadFile,
    selectNode,
    toggleNode,
    updateEditorSelection,
    clearSelection,
  };
}
